import type { OptionsMap } from '@openx/types/options.ts';
import { AudienceOption } from '@openx/types/targeting/audience.ts';
import type { UseAudienceOptionsFetchFn } from '@openx/types/targeting/targetingHookTypes.ts';
import { useDMPSegmentsOptionsFetch } from '@openx/utils/apiHooks/useDMPSegmentsOptionsFetch.ts';
import { useOpenAudiencesOptionsFetch } from '@openx/utils/apiHooks/useOpenAudiencesOptionsFetch.ts';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { useMemo } from 'react';

type RefetchFn = (options?: RefetchOptions) => Promise<QueryObserverResult<OptionsMap, Error>>;

type OptionsFetchResponse = {
  isLoading: boolean;
  options: OptionsMap;
  refetch?: RefetchFn;
};

export const useAudienceOptionsFetch: UseAudienceOptionsFetchFn = (
  accountUid: string,
  type: AudienceOption | null
): OptionsFetchResponse => {
  const audiences = useOpenAudiencesOptionsFetch(accountUid);
  const dmpSegments = useDMPSegmentsOptionsFetch(accountUid);
  return useMemo(() => {
    if (type && (type === AudienceOption.DMP_SEGMENTS || type === AudienceOption.OPEN_AUDIENCES)) {
      return {
        [AudienceOption.DMP_SEGMENTS]: {
          isLoading: dmpSegments.isDmpSegmentsLoading,
          options: dmpSegments.dmpSegments,
          refetch: dmpSegments.refetchDMPSegments,
        },
        [AudienceOption.OPEN_AUDIENCES]: {
          isLoading: audiences.isAudiencsesLoading,
          options: audiences.audiences,
          refetch: audiences.refetchAudiences,
        },
      }[type];
    }
    return {} as OptionsFetchResponse;
  }, [
    audiences.audiences,
    audiences.isAudiencsesLoading,
    audiences.refetchAudiences,
    dmpSegments.dmpSegments,
    dmpSegments.isDmpSegmentsLoading,
    dmpSegments.refetchDMPSegments,
    type,
  ]);
};
